import React, { useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';
import 'videojs-hotkeys';
import { assetUrl } from '../../config/asset-config';

// Component for rendering and controlling a video player using Video.js library
const VideoPlayer = React.forwardRef(({
    vidId, videoUrl, additionalOptions, onError, onReady, onFirstFrame, onPlay, onTime, onComplete, onPause, onVolumeChange,
}, ref) => {
    let completed = false; // Flag to track if video playback is completed
    let vidElement; // Reference to the video player element

    useEffect(() => {
        const initVideo = () => {
            // Check if Video.js library is loaded
            if (!window.videojs) {
                setTimeout(initVideo, 100);
                return;
            }
            // Default options for the video player
            const options = {
                controls: true,
                autoplay: false,
                preload: 'auto',
                aspectRatio: '4:3',
                controlBar: {
                    children: [
                        'playToggle',
                        'volumePanel',
                        'progressControl',
                        'currentTimeDisplay',
                        'timeDivider',
                        'durationDisplay',
                        'remainingTimeDisplay',
                        'captionsButton',
                        'fullscreenToggle',
                    ],
                },
            };

            // Merge additional options with default options
            if (additionalOptions) {
                Object.assign(options, additionalOptions);
            }

            // Set poster image for the video player
            options.poster = (options.thumbnail || options.image || assetUrl('assets/img/white.jpg')) + '?thumbnail';

            videoUrl += '?cache=videoJs';

            // Append video element to the DOM
            let appendString = `<video id="${vidId}" class="video-js tv-video-player" crossOrigin="anonymous" playsinline>`;
            appendString += `<source src="${videoUrl}" />`;

            // Add captions track if provided in additional options
            if (additionalOptions && additionalOptions.captions) {
                const autoplayCaptions = additionalOptions.autoplayCaptions ? 'default' : '';
                appendString += `<track kind="captions" src="${additionalOptions.captions}" srclang="en" label="Captions On" ${autoplayCaptions} />`;
            }

            appendString += '</video>';

            vidElement = document.getElementById(`${vidId}-wrapper`);
            if (vidElement) vidElement.innerHTML = appendString;

            // Initialize Video.js player
            const video = videojs(vidId, options);

            // Set up hotkeys and fullscreen change event handler for the video player
            video.ready(() => {
                // Determine seek duration based on video duration
                const seekDuration = video.duration() >= 60 ? 10 : 5;

                // Set up hotkeys for the video player
                video.hotkeys({
                    volumeStep: 0.1, // Step for adjusting volume
                    seekStep: seekDuration, // Step for seeking forward/backward in the video
                    enableModifiersForNumbers: false, // Disable using modifier keys for numeric input
                    customKeys: {
                        // Custom key for toggling captions visibility
                        captionKey: {
                            key: (event) => event.which === 67,
                            handler: (player) => {
                                const tracks = player.textTracks();
                                let track;

                                for (let i = 0; i < tracks.length; i++) {
                                    track = tracks[i];
                                    if (track.kind === 'captions') {
                                        track.mode = track.mode !== 'showing' ? 'showing' : 'hidden';
                                        return;
                                    }
                                }
                            },
                        },
                    },
                });

                // Event listener for fullscreen change event
                video.on('fullscreenchange', function () {
                    // Update tooltip text and title when entering fullscreen
                    if (video.isFullscreen()) {
                        const tooltip = document.querySelector('.vjs-fullscreen-control .vjs-control-text');
                        tooltip.textContent = 'Exit Fullscreen';
                        tooltip.parentElement.title = 'Exit Fullscreen';
                    }
                });
            });

            // Set up event listeners for various events on the video player instance

            videojs.players[vidId].on('error', (error) => {
                // Trigger onError callback if provided
                if (typeof onError === 'function') {
                    onError(error);
                }
            });

            videojs.players[vidId].on('ready', function () {
                // Trigger onReady callback if provided
                if (typeof onReady === 'function') {
                    onReady();
                }
            });

            videojs.players[vidId].on('timeupdate', function () {
                // Trigger onTime callback if provided with current position and duration
                if (typeof onTime === 'function') {
                    const timeObj = {
                        position: videojs.players[vidId].currentTime(),
                        duration: videojs.players[vidId].duration(),
                    };
                    onTime(timeObj);
                }
            });


            videojs.players[vidId].on('play', function () {
                // Reset completed flag and trigger onPlay callback if provided
                completed = false;
                if (typeof onPlay === 'function') {
                    onPlay();
                }
            });

            videojs.players[vidId].on('pause', function () {
                // Trigger onPause callback if provided
                if (typeof onPause === 'function') {
                    onPause();
                }
            });

            videojs.players[vidId].on('ended', function () {
                // Trigger onComplete callback if provided and not already completed
                if (typeof onComplete === 'function' && !completed) {
                    completed = true;
                    onComplete();
                }
            });

            videojs.players[vidId].on('volumechange', function () {
                // Trigger onVolumeChange callback if provided
                if (typeof onVolumeChange === 'function') {
                    onVolumeChange();
                }
            });

            // Fixes Safari bug where autoplay_captions doesn't work
            videojs.players[vidId].one('play', function () {
                // Trigger onFirstFrame callback if provided and disable captions if autoplayCaptions is false
                if (typeof onFirstFrame === 'function') {
                    onFirstFrame();
                }
                if (!!additionalOptions.autoplayCaptions === false) {
                    if (videojs.players[vidId].textTracks()[0]) {
                        video.textTracks()[0].mode = 'disabled';
                    }
                }
            });
        };

        // Initialize the video player when the component mounts
        initVideo();

        // Clean up by disposing the video player when the component unmounts
        return () => {
            close(vidId);
        };
    }, [vidId, videoUrl, additionalOptions]);

    // Additional methods

    // Close the video player by disposing of it
    const close = (vidId) => {
        if (videojs.players[vidId]) {
            videojs.players[vidId].dispose();
        }
    };

    // Load the video player
    const load = () => {
        videojs.players[vidId].load();
    };

    // Play the video
    const play = () => {
        seek(0);
        videojs.players[vidId].play();
    };

    // Seek to a specific time in the video
    const seek = (time) => {
        videojs.players[vidId].currentTime(time);
    };

    // Stop the video
    const stop = () => {
        if (!vidId || !videojs.players[vidId]) {
            return;
        }
        videojs.players[vidId].pause();
    };

    // Reset the video to the beginning
    const resetVideo = () => {
        videojs.players[vidId].pause();
        videojs.players[vidId].currentTime(0);
        videojs.players[vidId].trigger('ended');
        videojs.players[vidId].trigger('ready');
    };

    // Get the error object of the video player
    const getError = () => videojs.players[vidId].error();

    // Get the current time of the video player
    const getTime = () => videojs.players[vidId].currentTime();

    // Set the thumbnail image of the video player
    const setThumbnail = (thumbnail) => {
        videojs.players[vidId].poster(thumbnail);
    };

    // Set the volume of the video player
    const setVolume = (perc) => {
        if (perc > 1) {
            perc /= 100;
        }
        videojs.players[vidId].volume(perc);
    };

    // Set the audio state of the video player
    const setAudio = (bool) => {
        const q = $q.defer();
        const player = videojs.players[vidId];
        player.muted(!bool);
        if (bool) {
            if (player.volume() < 70) {
                player.volume(0.7);
            }
            $timeout(() => {
                const time = player.currentTime();
                const didStart = time > 0;
                q.resolve(didStart);
            }, 500);
        } else {
            q.resolve(true);
        }
        return q.promise;
    };

    // Imperative handle to expose methods to the parent component
    useImperativeHandle(ref, () => ({
        resetVideo,
        seek,
        setAudio,
        setVolume,
        setThumbnail,
        load,
        play,
        stop,
        getError,
        getTime,
    }));

    // Render null because VideoPlayer is not meant to render any UI
    return null;
});

VideoPlayer.propTypes = {
    vidId: PropTypes.string,
    videoUrl: PropTypes.string,
    additionalOptions: PropTypes.object,
    onError: PropTypes.func,
    onPlay: PropTypes.func,
    onReady: PropTypes.func,
    onFirstFrame: PropTypes.func,
    onTime: PropTypes.func,
    onComplete: PropTypes.func,
    onVolumeChange: PropTypes.func,
};

export default VideoPlayer;
